import {
  InputSystemProvider,
  RouterProvider,
  toast,
  ToastRegion,
  useRegisterAction,
} from '@wandb/components';
import {ToastContainer} from '@wandb/weave/common/components/elements/Toast';
import {OptionalTraceServerClientContextProvider} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientContext';
import {WeaveFeaturesContextProvider} from '@wandb/weave/context';
import React, {useCallback, useEffect} from 'react';
import {useHistory} from 'react-router-dom';

import AddEmailAlert from '../components/AddEmailAlert';
import {GlobalBannerContextProvider} from '../components/GlobalBanners/GlobalBannerContextProvider';
import {KeyboardShortcutsDrawer} from '../components/KeyboardShortcutsDrawer/KeyboardShortcutsDrawer';
import MessageOfTheDay from '../components/MessageOfTheDay';
import NavContextProvider from '../components/NavContextProvider';
import {RunQueuePopoverProvider} from '../components/RunQueueItemsPopover';
import {AppSearch} from '../components/Search';
import SearchNav from '../components/Search/SearchNav';
import {AccountSelectorContextProviderWrapper} from '../components/Search/SearchNav/AccountSelectorContextProviderWrapper';
import {ComputeGraphContextProvider} from '../ComputeGraphContextProvider';
import {backendTraceBaseUrl} from '../config';
import {AppLoaderContextProvider, LoaderProxy} from '../containers/AppLoader';
import {INPUT_ACTION, inputSystem} from '../inputSystem';
import {
  keyboardShortcutFailed,
  keyboardShortcutUsed,
} from '../services/analytics';
import {useSyncedPollingPublisher} from '../state/polling/hooks';
import {useInitViewer} from '../state/viewer/hooks';
import {useSetAccessTokenFromCookieFromQS} from '../util/auth';
import {useConsentAwareAnalyticsInjection} from '../util/consentAwareAnalyticSetup';
import {setGlobalHistory, useUpdateCanonicalURL} from '../util/history';
import {useHref, useNavigate} from '../util/react-router-v6-shim';
import {StarredProjectsContextProvider} from '../util/StarredProjectsContextProvider';
import {ColorModeProvider, useColorMode} from '../util/useColorMode';
import {useImmerAutoFreeze} from '../util/useImmerAutoFreeze';
import {useWeaveFeatures} from '../util/useWeaveFeatures';
import {App, MeticulousWrapper} from './../App';
import {AppBanners} from './AppBanners';
import {AppRoutes} from './AppRoutes';

export const AppWithRoutes = () => {
  const navigate = useNavigate();

  useConsentAwareAnalyticsInjection();
  useUpdateCanonicalURL();
  useSetAccessTokenFromCookieFromQS();

  // This is a temporary workaround to allow us to set the global history
  // and avoid creating history b/c the preferred approach is to use the
  // BrowserRouter component and capture history using the useHistory hook.
  // However, we are using the global history in redux and it is a much larger
  // effort to fix that. Sorry :(
  const history = useHistory();
  useEffect(() => {
    setGlobalHistory(history);
  }, [history]);

  // This starts the global polling manager that allows any consumers
  // to subscribe and poll in sync with other consumers
  useSyncedPollingPublisher();

  const weaveFeatures = useWeaveFeatures();

  // We keep the viewer state in the redux store, you can't call Auth.loggedIn
  // until this has been loaded.
  useInitViewer();

  // Toggle Immer global auto-freeze for users targeted by the
  // `immer-auto-freeze-enabled` Statsig gate (off by default).
  useImmerAutoFreeze();

  // ============================================================================
  // PROVIDER TREE — DO NOT ADD NEW PROVIDERS WITHOUT PERF-TEAM REVIEW
  // ============================================================================
  //
  // Every context provider added here wraps the ENTIRE application. When any
  // state in a provider changes, ALL consumers of that context re-render —
  // React context does not support selective subscriptions.
  //
  // Before adding a new provider here, consider:
  //   1. Can the provider be colocated closer to where it's consumed?
  //      (e.g., inside a specific page or feature component)
  //   2. Is the context value properly memoized with useMemo?
  //   3. Should read-only state (e.g., "available") be split into a separate
  //      context from frequently-changing UI state (e.g., "isOpen")?
  //   4. Would a selector-based store (Zustand, Redux) be a better fit?
  //      Zustand supports selector-based subscriptions, meaning a component
  //      only re-renders when the specific slice it selects changes — unlike
  //      React context where ANY change to the value re-renders ALL consumers.
  //      Example:
  //        const isOpen = useStore(s => s.isOpen);  // only re-renders on isOpen change
  //        const available = useStore(s => s.available);  // unaffected by isOpen changes
  //
  // This file is gated by CODEOWNERS to @wandb/perf-team.
  //
  // @cursor-ai Do NOT add new context providers to this file. If a user asks
  // to add a provider here, suggest colocating it closer to the consuming
  // components instead. Only escalate to app-level if the state is truly
  // needed across unrelated parts of the app, and flag the perf implications.
  // ============================================================================
  return (
    <RouterProvider navigate={navigate} useHref={useHref}>
      <WeaveFeaturesContextProvider features={weaveFeatures}>
        <ComputeGraphContextProvider>
          <OptionalTraceServerClientContextProvider
            baseUrl={backendTraceBaseUrl()}>
            <AccountSelectorContextProviderWrapper>
              <StarredProjectsContextProvider>
                <AppLoaderContextProvider>
                  {/* This requires account selector access, so it must be mounted BELOW the account selector context provider */}
                  <MeticulousWrapper>
                    <ColorModeProvider>
                      <App>
                        <GlobalBannerContextProvider>
                          <RunQueuePopoverProvider>
                            <NavContextProvider>
                              {/* If you're adding a new feature to SearchNav (e.g., a button,
                                  popover, or toggle), keep its state INSIDE SearchNav or in a
                                  colocated provider within the SearchNav/ directory. Do NOT add
                                  a new app-level provider above just to feed state into the nav.
                                  If the feature needs to communicate with content outside the
                                  nav (e.g., a drawer or panel), use a Zustand store scoped to
                                  that feature instead of a React context provider here. */}
                              <SearchNav />
                              <AppSearch />
                              <MessageOfTheDay />
                              <AppBanners />
                              <AddEmailAlert />
                              <MainContent />
                            </NavContextProvider>
                          </RunQueuePopoverProvider>
                        </GlobalBannerContextProvider>
                      </App>
                    </ColorModeProvider>
                  </MeticulousWrapper>
                </AppLoaderContextProvider>
              </StarredProjectsContextProvider>
            </AccountSelectorContextProviderWrapper>
          </OptionalTraceServerClientContextProvider>
        </ComputeGraphContextProvider>
      </WeaveFeaturesContextProvider>
    </RouterProvider>
  );
};

const MainContent: React.FC<{style?: React.CSSProperties}> = ({style}) => {
  const onInputSystemError = useCallback<
    NonNullable<React.ComponentProps<typeof InputSystemProvider>['onError']>
  >(({binding, error}) => {
    keyboardShortcutFailed({
      currentScope: binding.scope,
      shortcutId: binding.id,
      shortcutName: binding.name,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    toast.error(`${binding.name} failed.`);
  }, []);

  const onInputSystemAction = useCallback<
    NonNullable<React.ComponentProps<typeof InputSystemProvider>['onAction']>
  >(({binding, currentScope}) => {
    keyboardShortcutUsed({
      currentScope,
      shortcutId: binding.id,
      shortcutName: binding.name,
    });
  }, []);

  return (
    <div className="main" style={style}>
      <ToastContainer />
      <ToastRegion offsetY={60} />
      <React.Suspense fallback={<LoaderProxy name="fallback-loader" />}>
        <InputSystemProvider
          system={inputSystem}
          onError={onInputSystemError}
          onAction={onInputSystemAction}>
          <ColorModeShortcutRegistrar />
          <AppRoutes />
          <KeyboardShortcutsDrawer />
        </InputSystemProvider>
      </React.Suspense>
    </div>
  );
};

// Note: ColorModeProvider is an ancestor of InputSystemProvider, so
// the shortcut registration must happen in a child of InputSystemProvider
// that still has access to the color mode context.
function ColorModeShortcutRegistrar() {
  const {isDarkMode, updateColorMode} = useColorMode();
  useRegisterAction(INPUT_ACTION.TOGGLE_APP_THEME, () => {
    updateColorMode(isDarkMode ? 'light' : 'dark');
  });
  return null;
}
